import React, { useState, memo } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircleIcon, ArrowRightIcon, StarIcon, MessageCircleIcon, LinkIcon, RefreshCwIcon, ShieldCheckIcon, ThumbsUpIcon, ChevronDownIcon, BuildingIcon, ZapIcon } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';
import { SEOHead } from '../components/SEOHead';
export function ReputationManagementPage() {
  const faqs = [{
    question: 'How does automated review requesting work?',
    answer: 'When a job is marked as complete in your CRM or invoicing software, our system automatically sends a polite, customized text message and email to the customer thanking them for their business and asking for a review with a direct link.'
  }, {
    question: 'Can I respond to reviews from one dashboard?',
    answer: 'Yes. You can view and reply to reviews from Google, Facebook, and major review platforms all from a single unified dashboard or directly from our mobile app. No more logging into multiple accounts.'
  }, {
    question: 'What platforms do you manage reviews on?',
    answer: 'We focus on the platforms that matter most for local service businesses: Google Business Profile, Facebook, and the major review sites for your industry. We can also integrate with industry-specific platforms depending on your needs.'
  }, {
    question: 'Do I need to be on every review platform?',
    answer: 'No. We analyze your specific market and industry to determine where your customers are actually looking. Usually, dominating Google plus one or two key review platforms is all a local service business needs to win their market.'
  }];
  const FAQItem = ({
    question,
    answer
  }: {
    question: string;
    answer: string;
  }) => {
    const [isOpen, setIsOpen] = useState(false);
    return <div className="border-b border-gray-800 last:border-0" data-id="element-2245">
        <button onClick={() => setIsOpen(!isOpen)} className="w-full py-6 flex items-center justify-between text-left focus:outline-none group" data-id="element-2246">
          <span className="text-lg font-bold text-white group-hover:text-yellow-500 transition-colors" data-id="element-2247">
            {question}
          </span>
          <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform duration-300 flex-shrink-0 ml-4 ${isOpen ? 'rotate-180 text-yellow-500' : ''}`} data-id="element-2248" />
        </button>
        <AnimatePresence data-id="element-2249">
          {isOpen && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.3,
          ease: 'easeInOut'
        }} className="overflow-hidden" data-id="element-2250">
              <p className="pb-6 text-gray-400 leading-relaxed" data-id="element-2251">{answer}</p>
            </motion.div>}
        </AnimatePresence>
      </div>;
  };
  return <div className="bg-viridia-black min-h-screen" data-id="element-2252">
      <SEOHead title="Reputation Management | Automate Reviews | Viridia Analytics" description="Turn happy customers into public proof. Manage reviews across platforms, automate review requests, and build trust for your local service business." canonical="/reputation-management" keywords="reputation management Phoenix, marketing agency phoenix, HVAC, painters, landscapers, contractors, window installers, automated review requests, get more Google reviews, local business reputation, review management software" faq={faqs} data-id="element-2253" />

      {/* HERO SECTION */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden lg:min-h-[90vh] flex items-center" data-id="element-2254">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none" data-id="element-2255">
          <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] rounded-full bg-yellow-500/10 blur-[120px] animate-pulse-slow" data-id="element-2256"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full" data-id="element-2257">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-2258">
            {/* Hero Content */}
            <div className="max-w-2xl" data-id="element-2259">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5
            }} data-id="element-2260">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-yellow-500/10 border border-yellow-500/20 text-yellow-500 text-sm font-semibold tracking-wide mb-6" data-id="element-2261">
                  REPUTATION MANAGEMENT
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6 tracking-tight" data-id="element-2262">
                  Turn Happy Customers Into{' '}
                  <span className="text-yellow-500" data-id="element-2263">Public Proof</span>
                </h1>
                <p className="text-lg text-gray-400 mb-8 leading-relaxed" data-id="element-2264">
                  Reviews are not just vanity metrics; they are conversion
                  leverage. We automate review requests after completed jobs to
                  build trust and help you win more local searches.
                </p>
                <div className="flex flex-col sm:flex-row gap-4" data-id="element-2265">
                  <Link to="/contact" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-viridia text-white font-semibold hover:bg-viridia-dark transition-colors duration-300 shadow-[0_0_20px_rgba(16,185,129,0.2)]" data-id="element-2266">
                    Book a Strategy Call
                  </Link>
                  <Link to="/pricing" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-transparent border border-gray-600 text-white font-semibold hover:bg-white/5 transition-colors duration-300" data-id="element-2267">
                    View Packages
                  </Link>
                </div>
              </motion.div>
            </div>

            {/* Hero Visual - Reputation Control Center */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="relative hidden lg:block h-[500px]" data-id="element-2268">
              <div className="absolute inset-0 bg-gradient-to-br from-gray-900 to-viridia-surface rounded-2xl border border-gray-800 shadow-2xl overflow-hidden p-6 flex flex-col" data-id="element-2269">
                <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-800" data-id="element-2270">
                  <div className="text-sm font-bold text-white flex items-center" data-id="element-2271">
                    <StarIcon className="w-4 h-4 text-yellow-500 mr-2 fill-current" data-id="element-2272" />
                    Reputation Control Center
                  </div>
                  <div className="bg-green-500/10 text-green-400 text-xs px-2 py-1 rounded font-medium" data-id="element-2273">
                    System Active
                  </div>
                </div>

                <div className="flex-grow relative" data-id="element-2274">
                  {/* Floating Review Cards */}
                  <motion.div initial={{
                  y: 20,
                  opacity: 0
                }} animate={{
                  y: 0,
                  opacity: 1
                }} transition={{
                  delay: 0.5
                }} className="absolute top-4 left-4 right-12 bg-white rounded-lg p-4 shadow-lg z-20" data-id="element-2275">
                    <div className="flex justify-between items-start mb-2" data-id="element-2276">
                      <div className="flex text-yellow-400" data-id="element-2277">
                        {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-4 h-4 fill-current" data-id="element-2278" />)}
                      </div>
                      <span className="text-xs text-gray-400 font-medium" data-id="element-2279">
                        Google • 2h ago
                      </span>
                    </div>
                    <p className="text-sm text-gray-800 font-medium mb-1" data-id="element-2280">
                      "They responded instantly and fixed the issue the same
                      day. Highly recommend!"
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-2281">- Sarah Jenkins</p>
                  </motion.div>

                  <motion.div initial={{
                  y: 20,
                  opacity: 0
                }} animate={{
                  y: 0,
                  opacity: 1
                }} transition={{
                  delay: 0.7
                }} className="absolute top-32 left-12 right-4 bg-viridia-black border border-gray-700 rounded-lg p-4 shadow-xl z-10" data-id="element-2282">
                    <div className="flex justify-between items-start mb-2" data-id="element-2283">
                      <div className="flex text-yellow-500" data-id="element-2284">
                        {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-4 h-4 fill-current" data-id="element-2285" />)}
                      </div>
                      <span className="text-xs text-gray-500 font-medium" data-id="element-2286">
                        Review Platform • 1h ago
                      </span>
                    </div>
                    <p className="text-sm text-gray-300 font-medium mb-1" data-id="element-2287">
                      "Very professional crew. The estimate was accurate and the
                      work was top notch."
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-2288">- Michael T.</p>
                  </motion.div>

                  {/* Automation Flow Indicator */}
                  <div className="absolute bottom-4 left-0 right-0 flex justify-center" data-id="element-2289">
                    <div className="bg-viridia-surface border border-gray-800 rounded-full px-4 py-2 flex items-center gap-3 shadow-lg" data-id="element-2290">
                      <div className="flex items-center gap-1 text-xs text-gray-400" data-id="element-2291">
                        <CheckCircleIcon className="w-3 h-3 text-viridia" data-id="element-2292" /> Job
                        Complete
                      </div>
                      <ArrowRightIcon className="w-3 h-3 text-gray-600" data-id="element-2293" />
                      <div className="flex items-center gap-1 text-xs text-gray-400" data-id="element-2294">
                        <MessageCircleIcon className="w-3 h-3 text-blue-400" data-id="element-2295" />{' '}
                        SMS Sent
                      </div>
                      <ArrowRightIcon className="w-3 h-3 text-gray-600" data-id="element-2296" />
                      <div className="flex items-center gap-1 text-xs text-yellow-500 font-bold" data-id="element-2297">
                        <StarIcon className="w-3 h-3 fill-current" data-id="element-2298" /> 5-Star
                        Review
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* THE PROBLEM SECTION */}
      <section className="py-24 bg-viridia-surface border-t border-gray-800" data-id="element-2299">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2300">
          <ScrollReveal data-id="element-2301">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2302">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-2303">
                Why Reviews Matter Financially
              </h2>
              <p className="text-xl text-gray-400" data-id="element-2304">
                In local services, trust is everything. If your competitor has
                100 more reviews than you, they are getting the call.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2305">
            {[{
            icon: <StarIcon className="w-6 h-6 text-white" data-id="element-2306" />,
            title: 'Conversion Leverage',
            desc: 'A strong review profile increases the conversion rate of every ad dollar you spend.',
            color: 'bg-yellow-500'
          }, {
            icon: <ShieldCheckIcon className="w-6 h-6 text-white" data-id="element-2307" />,
            title: 'Local Trust',
            desc: 'Customers hire the business that looks the most reliable. Reviews are public proof of your reliability.',
            color: 'bg-blue-500'
          }, {
            icon: <RefreshCwIcon className="w-6 h-6 text-white" data-id="element-2308" />,
            title: 'Automated Workflows',
            desc: 'We tie review requests directly to your CRM. When a job is done, the request goes out automatically.',
            color: 'bg-viridia'
          }].map((feature, i) => <ScrollReveal key={i} delay={i * 0.1} data-id="element-2309">
                <div className="bg-viridia-black rounded-2xl p-8 border border-gray-800 h-full hover:border-gray-700 transition-colors" data-id="element-2310">
                  <div className={`w-12 h-12 rounded-xl ${feature.color}/20 flex items-center justify-center mb-6`} data-id="element-2311">
                    <div className={`w-8 h-8 rounded-full ${feature.color} flex items-center justify-center`} data-id="element-2312">
                      {feature.icon}
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-2313">
                    {feature.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-2314">
                    {feature.desc}
                  </p>
                </div>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS SECTION */}
      <section className="py-24 bg-viridia-black" data-id="element-2315">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2316">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-2317">
            <ScrollReveal data-id="element-2318">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-2319">
                The Automated Review Loop
              </h2>
              <p className="text-lg text-gray-400 mb-8 leading-relaxed" data-id="element-2320">
                Asking for reviews manually is awkward and inconsistent. We
                build a system that asks for you, exactly when the customer is
                happiest.
              </p>

              <div className="space-y-8" data-id="element-2321">
                <div className="flex items-start" data-id="element-2322">
                  <div className="flex-shrink-0 mr-4" data-id="element-2323">
                    <div className="w-10 h-10 rounded-full bg-viridia/10 border border-viridia/30 flex items-center justify-center text-viridia font-bold" data-id="element-2324">
                      1
                    </div>
                  </div>
                  <div data-id="element-2325">
                    <h3 className="text-xl font-bold text-white mb-2" data-id="element-2326">
                      Trigger on Completion
                    </h3>
                    <p className="text-gray-400" data-id="element-2327">
                      When your team marks a job as complete or paid in your
                      CRM, the automation begins.
                    </p>
                  </div>
                </div>

                <div className="flex items-start" data-id="element-2328">
                  <div className="flex-shrink-0 mr-4" data-id="element-2329">
                    <div className="w-10 h-10 rounded-full bg-viridia/10 border border-viridia/30 flex items-center justify-center text-viridia font-bold" data-id="element-2330">
                      2
                    </div>
                  </div>
                  <div data-id="element-2331">
                    <h3 className="text-xl font-bold text-white mb-2" data-id="element-2332">
                      Smart Request Sent
                    </h3>
                    <p className="text-gray-400" data-id="element-2333">
                      A personalized text and email are sent thanking them for
                      their business and providing a direct link to review.
                    </p>
                  </div>
                </div>

                <div className="flex items-start" data-id="element-2334">
                  <div className="flex-shrink-0 mr-4" data-id="element-2335">
                    <div className="w-10 h-10 rounded-full bg-viridia/10 border border-viridia/30 flex items-center justify-center text-viridia font-bold" data-id="element-2336">
                      3
                    </div>
                  </div>
                  <div data-id="element-2337">
                    <h3 className="text-xl font-bold text-white mb-2" data-id="element-2338">
                      Monitor & Respond
                    </h3>
                    <p className="text-gray-400" data-id="element-2339">
                      New reviews appear in your unified dashboard, allowing you
                      to respond quickly and professionally.
                    </p>
                  </div>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal direction="left" data-id="element-2340">
              <div className="bg-viridia-surface rounded-2xl p-8 border border-gray-800 shadow-2xl" data-id="element-2341">
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-800" data-id="element-2342">
                  <h3 className="text-white font-bold" data-id="element-2343">Automated SMS</h3>
                  <span className="text-xs text-gray-500" data-id="element-2344">Sent at 4:30 PM</span>
                </div>
                <div className="space-y-4" data-id="element-2345">
                  <div className="flex justify-end" data-id="element-2346">
                    <div className="bg-blue-600 text-white p-3 rounded-2xl rounded-tr-sm max-w-[80%] text-sm" data-id="element-2347">
                      Hi John, thanks for choosing Viridia HVAC! We hope you're
                      happy with your new AC unit. If you have a minute, we'd
                      love your feedback: https://g.page/r/review
                    </div>
                  </div>
                  <div className="flex justify-start" data-id="element-2348">
                    <div className="bg-gray-800 text-white p-3 rounded-2xl rounded-tl-sm max-w-[80%] text-sm" data-id="element-2349">
                      You guys were great. Just left a 5-star review!
                    </div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Best Fit Section */}
      <section className="py-20 bg-viridia-surface border-t border-gray-800" data-id="element-2350">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2351">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-2352">
            <ScrollReveal data-id="element-2353">
              <h2 className="text-3xl font-bold text-white mb-6" data-id="element-2354">
                Who is this for?
              </h2>
              <p className="text-gray-400 mb-6 leading-relaxed" data-id="element-2355">
                Our reputation management system is designed for local service
                businesses that want to turn their happy customers into a
                marketing asset.
              </p>
              <ul className="space-y-4" data-id="element-2356">
                <li className="flex items-start gap-3" data-id="element-2357">
                  <BuildingIcon className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-0.5" data-id="element-2358" />
                  <div data-id="element-2359">
                    <h4 className="font-bold text-white" data-id="element-2360">Local Contractors</h4>
                    <p className="text-sm text-gray-400" data-id="element-2361">
                      Businesses that rely on local trust to win jobs.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3" data-id="element-2362">
                  <ZapIcon className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-0.5" data-id="element-2363" />
                  <div data-id="element-2364">
                    <h4 className="font-bold text-white" data-id="element-2365">
                      Businesses Using Our CRM
                    </h4>
                    <p className="text-sm text-gray-400" data-id="element-2366">
                      The review automation ties perfectly into our CRM,
                      triggering requests automatically when jobs are completed.
                    </p>
                  </div>
                </li>
              </ul>
            </ScrollReveal>
            <ScrollReveal delay={0.2} data-id="element-2367">
              <div className="bg-viridia-black border border-gray-800 rounded-2xl p-8" data-id="element-2368">
                <h3 className="text-xl font-bold text-white mb-4" data-id="element-2369">
                  Related Services
                </h3>
                <div className="space-y-4" data-id="element-2370">
                  <Link to="/crm-business-tools" className="block p-4 bg-viridia-surface rounded-xl border border-gray-800 hover:border-viridia/50 transition-colors" data-id="element-2371">
                    <div className="font-bold text-white mb-1" data-id="element-2372">
                      CRM & Business Tools
                    </div>
                    <div className="text-sm text-gray-400" data-id="element-2373">
                      Trigger review requests automatically from your pipeline.
                    </div>
                  </Link>
                  <Link to="/web-design-seo" className="block p-4 bg-viridia-surface rounded-xl border border-gray-800 hover:border-viridia/50 transition-colors" data-id="element-2374">
                    <div className="font-bold text-white mb-1" data-id="element-2375">
                      Conversion Websites
                    </div>
                    <div className="text-sm text-gray-400" data-id="element-2376">
                      Showcase your 5-star reviews on a site built to convert.
                    </div>
                  </Link>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-24 bg-viridia-black border-t border-gray-800" data-id="element-2377">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-16" data-id="element-2378">
          <ScrollReveal data-id="element-2379">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-2380">
              Frequently Asked Questions
            </h2>
          </ScrollReveal>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2381">
          <ScrollReveal delay={0.2} data-id="element-2382">
            <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-6 md:p-8" data-id="element-2383">
              {faqs.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} data-id="element-2384" />)}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-24 bg-viridia-surface border-t border-gray-800" data-id="element-2385">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2386">
          <ScrollReveal data-id="element-2387">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-2388">
              Start building local trust
            </h2>
            <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto" data-id="element-2389">
              Book a strategy call to see how we can automate your review
              requests and help you win more local searches.
            </p>
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold rounded-lg bg-viridia text-white hover:bg-viridia-dark transition-all duration-300 shadow-[0_0_20px_rgba(16,185,129,0.2)]" data-id="element-2390">
              Book a Strategy Call <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-2391" />
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}